import { can } from "../rbac/matcher";
import {
  MANAGE_PERMISSION_ROLE_MAP,
  MANAGE_PERMISSION_HIERARCHY,
} from "../rbac/permissions";

const APP_ID = import.meta.env.VITE_PUBLIC_APP_ID || "supplier_portal";

/**
 * Parses rolePermissions from the user API response.
 * Filters by APP_ID and builds allow/deny Sets.
 *
 * Supports two API formats:
 * - Flat: [{ appId, permission, type }]  (actual API response)
 * - Nested: [{ appId, permissions: [{ key, value }] }]  (legacy format)
 *
 * @param {Array} rolePermissions
 * @returns {{ allowSet: Set<string>, denySet: Set<string> }}
 */
export const parsePermissions = (rolePermissions) => {
  const allowSet = new Set();
  const denySet = new Set();

  if (!Array.isArray(rolePermissions)) return { allowSet, denySet };

  for (const entry of rolePermissions) {
    if (entry.appId !== APP_ID) continue;

    if (entry.permission != null && entry.type != null) {
      if (entry.type === "allow") allowSet.add(entry.permission);
      else if (entry.type === "deny") denySet.add(entry.permission);
    } else if (Array.isArray(entry.permissions)) {
      for (const perm of entry.permissions) {
        const permKey = perm.key ?? perm.permission;
        const permValue = perm.value ?? perm.type;
        if (permValue === "allow") allowSet.add(permKey);
        else if (permValue === "deny") denySet.add(permKey);
      }
    }
  }

  return { allowSet, denySet };
};

/**
 * Default-deny permission check with wildcard support.
 * Delegates to the RBAC matcher which evaluates:
 *   deny (exact → wildcard) → allow (exact → wildcard) → default deny.
 *
 * @param {string} permKey - Resource or permission key (e.g., "promo", "user.edit_self_profile")
 * @param {Set<string>} allowSet - (unused in deny-explicit model, kept for API compatibility)
 * @param {Set<string>} denySet
 * @returns {boolean} true if allowed
 */
export const isPermissionAllowed = (permKey, allowSet, denySet) => {
  return can(permKey, allowSet, denySet);
};

/**
 * Checks if the current user can manage (edit) a user with the given role.
 * Requires user.edit_profile as a general gate, then checks role-specific manage permissions.
 *
 * @param {string} targetRoleName - The role name of the user being edited
 * @param {Set<string>} allowSet
 * @param {Set<string>} denySet
 * @returns {boolean}
 */
export const canManageUserByRole = (targetRoleName, allowSet, denySet) => {
  // General gate: user.edit_profile must be allowed
  if (!isPermissionAllowed("user.edit_profile", allowSet, denySet))
    return false;

  // Look up which manage permission controls this target role
  let targetPermKey = null;
  for (const [permKey, roles] of Object.entries(MANAGE_PERMISSION_ROLE_MAP)) {
    if (roles.includes(targetRoleName)) {
      targetPermKey = permKey;
      break;
    }
  }

  // Role not in any map -> allowed (if user.edit_profile passed)
  if (!targetPermKey) return true;

  // Target permission must be allowed
  if (!isPermissionAllowed(targetPermKey, allowSet, denySet)) return false;

  // Hierarchy enforcement: an explicit deny on a lower-level permission blocks higher levels
  const targetLevel = MANAGE_PERMISSION_HIERARCHY.indexOf(targetPermKey);
  for (let i = 0; i < targetLevel; i++) {
    if (denySet.has(MANAGE_PERMISSION_HIERARCHY[i])) {
      return false;
    }
  }
  return true;
};
